import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from "axios";
import { resolveApiBaseUrl } from "@/config/api-base-url";
import { getStoredAccessToken } from "@/lib/auth-token-storage";
import {
  ensureFreshAccessToken,
  handleUnauthorized,
  refreshSessionOnce,
} from "@/services/http/session-refresh";
import { fetchCsrfToken, invalidateCsrfCache } from "@/services/http/csrf-token";
import { isTermsNotAcceptedBody, notifyTermsRequired } from "@/services/http/terms-required";

/**
 * AUTH-6: one cached, single-flight CSRF token shared with apiFetch. Fetching a new token per
 * mutation replaced the CSRF cookie under requests already in flight, so overlapping posts
 * (lesson progress + heartbeat) failed at random.
 */
async function csrfTokenOrNull(force: boolean): Promise<string | null> {
  try {
    return await fetchCsrfToken(force);
  } catch {
    return null;
  }
}

type RetriableConfig = InternalAxiosRequestConfig & { _retry?: boolean; _csrfRetry?: boolean };

export function createHttpClient(): AxiosInstance {
  const client = axios.create({
    baseURL: resolveApiBaseUrl(),
    withCredentials: true,
  });

  client.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
    const url = config.url ?? "";
    await ensureFreshAccessToken(url);
    const token = getStoredAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    const method = config.method?.toUpperCase() ?? "GET";
    const isMutating = ["POST", "PUT", "PATCH", "DELETE"].includes(method);
    // Only cookie-less auth entrypoints are exempt — /v1/auth/register still needs CSRF.
    // The code steps (AUTH-003) are bound to a signed challenge token instead.
    const csrfExempt = url.startsWith("/auth/login") || url.startsWith("/auth/verify-email-otp");

    if (isMutating && !csrfExempt) {
      const csrf = await csrfTokenOrNull(Boolean((config as RetriableConfig)._csrfRetry));
      if (csrf) {
        config.headers["x-csrf-token"] = csrf;
      }
    }

    return config;
  });

  // C2: 401 → shared single-flight refresh → retry once (the request interceptor attaches the
  // new token). A refused refresh signs out and redirects inside handleUnauthorized.
  client.interceptors.response.use(
    (response) => response,
    async (error) => {
      const original = error?.config as RetriableConfig | undefined;
      if (
        original &&
        !original._retry &&
        error.response?.status === 401 &&
        (await handleUnauthorized(original.url ?? ""))
      ) {
        original._retry = true;
        return client(original);
      }
      // AUTH-6: the cookie was rotated under us (another tab / client) — one retry, fresh token.
      if (
        original &&
        !original._csrfRetry &&
        error.response?.status === 403 &&
        error.response?.data?.error?.code === "CSRF_INVALID"
      ) {
        original._csrfRetry = true;
        invalidateCsrfCache();
        return client(original);
      }
      // H4 review F11: the terms changed mid-session — the accept screen comes up.
      if (error.response?.status === 403 && isTermsNotAcceptedBody(error.response?.data)) {
        notifyTermsRequired();
      }
      return Promise.reject(error);
    },
  );

  return client;
}

export const httpClient = createHttpClient();

/** Re-issues the session from the server, picking up a role that changed since sign-in. */
export async function refreshSession(): Promise<boolean> {
  return (await refreshSessionOnce()) === "refreshed";
}
